import api from './api';
import { format } from 'date-fns';

export const getAppointments = async (startDate, endDate) => {
  try {
    // En producción o desarrollo sin backend, usa datos mock
    if (process.env.NODE_ENV === 'production' || process.env.REACT_APP_USE_MOCK === 'true') {
      console.log('Using mock appointments data');
      return getMockAppointments(startDate, endDate);
    }
    
    // Formatear fechas para enviar al API
    const params = {
      start_date: format(startDate, 'yyyy-MM-dd'),
      end_date: format(endDate, 'yyyy-MM-dd')
    };
    
    const response = await api.get('/appointments', { params });
    return response.data;
  } catch (error) {
    console.error('Error en getAppointments:', error);
    // Si hay un error, devuelve datos mock en lugar de fallar
    return getMockAppointments(startDate, endDate);
  }
};

// Nueva función para obtener citas por cliente
export const getAppointmentsByClient = async (clientId) => {
  try {
    // En producción o desarrollo sin backend, usa datos mock
    if (process.env.NODE_ENV === 'production' || process.env.REACT_APP_USE_MOCK === 'true') {
      console.log('Using mock client appointments data');
      return getMockClientAppointments(clientId);
    }
    
    const response = await api.get(`/clients/${clientId}/appointments`);
    return response.data;
  } catch (error) {
    console.error(`Error en getAppointmentsByClient para cliente ${clientId}:`, error);
    // Si hay un error, devuelve datos mock
    return getMockClientAppointments(clientId);
  }
};

// Nueva función para marcar la asistencia a una cita
export const markAppointmentAttendance = async (id, attended) => {
  try {
    // En producción o desarrollo sin backend, usa datos mock
    if (process.env.NODE_ENV === 'production' || process.env.REACT_APP_USE_MOCK === 'true') {
      console.log(`Marking appointment ${id} as ${attended ? 'attended' : 'not attended'} (mock)`);
      // Simula un retraso para dar tiempo a que se actualice la UI
      await new Promise(resolve => setTimeout(resolve, 300));
      return { id, attended };
    }
    
    const response = await api.patch(`/appointments/${id}/attendance`, { attended });
    return response.data;
  } catch (error) {
    console.error(`Error en markAppointmentAttendance para cita ${id}:`, error);
    throw error;
  }
};

// Esta función es para uso de testing y desarrollo
export const getMockAppointments = (startDate, endDate) => {
  // Generar algunas citas de ejemplo dentro del rango de fechas
  const result = [];
  
  // Número de citas a generar
  const numAppointments = 20;
  
  // Tipos de citas de ejemplo
  const appointmentTypes = ['Funcional', 'Musculación', 'Cardio', 'Evaluación'];
  
  // Nombres de ejemplo
  const clientNames = ['María Figueroa', 'Juan Ledesma', 'Sofía Álvarez', 'Nicolás Varela', 'Carla Domínguez', 'Javier Altszyler'];
  
  // Horas de ejemplo (formato 24h)
  const times = [9, 10, 11, 13, 15, 16, 17, 18];
  
  // Estado local para citas marcadas como asistidas/no asistidas
  const storedAttendance = localStorage.getItem('appointmentAttendance');
  const attendanceData = storedAttendance ? JSON.parse(storedAttendance) : {};
  
  for (let i = 0; i < numAppointments; i++) {
    // Generar fecha aleatoria dentro del rango
    const startTime = startDate.getTime();
    const endTime = endDate.getTime();
    const randomTime = startTime + Math.random() * (endTime - startTime);
    const date = new Date(randomTime);
    
    // Establecer hora aleatoria
    const randomHour = times[Math.floor(Math.random() * times.length)];
    date.setHours(randomHour, 0, 0, 0);
    
    // Elegir tipo aleatorio
    const typeIndex = Math.floor(Math.random() * appointmentTypes.length);
    const type = appointmentTypes[typeIndex];
    
    // Elegir cliente aleatorio
    const clientIndex = Math.floor(Math.random() * clientNames.length);
    const clientName = clientNames[clientIndex];
    
    // Generar ID único para la cita
    const id = `app-${date.getTime()}-${i}`;
    
    // Verificar si hay datos guardados de asistencia
    const attended = attendanceData[id] !== undefined ? 
                    attendanceData[id] : 
                    (Math.random() > 0.7 ? Math.random() > 0.5 : undefined);
    
    // Generar cita
    result.push({
      id: id,
      title: `${type} - ${randomHour} hs`,
      type: type,
      clientId: clientIndex + 1,
      clientName: clientName,
      date: date.toISOString(),
      time: `${randomHour}:00`,
      calendarUrl: 'https://calendar.google.com/calendar/u/0/r/day/' + 
                   date.getFullYear() + '/' + 
                   (date.getMonth() + 1) + '/' + 
                   date.getDate(),
      attended: attended
    });
  }
  
  return result;
};

// Función mock para obtener citas de un cliente específico
export const getMockClientAppointments = (clientId) => {
  // Generar historial de citas para un cliente específico
  const result = [];
  
  // Tipos de citas de ejemplo
  const appointmentTypes = ['Funcional', 'Musculación', 'Cardio', 'Evaluación'];
  
  // Entrenadores
  const trainers = ['Nicolás Varela', 'Sofía Álvarez', 'Javier Altszyler'];
  
  // Horas de ejemplo (formato 24h)
  const times = ['09:00', '10:00', '15:00', '17:00', '18:00'];
  
  // Estado local para citas marcadas como asistidas/no asistidas
  const storedAttendance = localStorage.getItem('appointmentAttendance');
  const attendanceData = storedAttendance ? JSON.parse(storedAttendance) : {};
  
  // Generar citas de los últimos 3 meses
  const today = new Date();
  const numAppointments = 8 + Math.floor(Math.random() * 8); // Entre 8 y 15 citas
  
  for (let i = 0; i < numAppointments; i++) {
    // Generar fecha aleatoria en los últimos 3 meses
    const daysAgo = Math.floor(Math.random() * 90); // 0 a 90 días atrás
    const date = new Date();
    date.setDate(today.getDate() - daysAgo);
    
    // Formatear fecha
    const formattedDate = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
    
    // Elegir tiempo aleatorio
    const time = times[Math.floor(Math.random() * times.length)];
    
    // Elegir tipo aleatorio
    const type = appointmentTypes[Math.floor(Math.random() * appointmentTypes.length)];
    
    // Elegir entrenador aleatorio
    const trainer = trainers[Math.floor(Math.random() * trainers.length)];
    
    // Generar ID único para la cita
    const id = `app-client-${clientId}-${date.getTime()}-${i}`;
    
    // Verificar si hay datos guardados de asistencia
    const attended = attendanceData[id] !== undefined ? 
                    attendanceData[id] : 
                    (Math.random() > 0.2);
    
    // Generar notas (solo para algunas citas)
    let notes = null;
    if (Math.random() > 0.7) {
      const possibleNotes = [
        'Progreso notable en fuerza',
        'Mejoró técnica en sentadillas',
        'Se quejó de molestia en rodilla',
        'Aumentó peso en press de banca',
        'Completó rutina sin descansos adicionales'
      ];
      notes = possibleNotes[Math.floor(Math.random() * possibleNotes.length)];
    }
    
    // Generar cita
    result.push({
      id: id,
      date: formattedDate,
      time: time,
      type: type,
      trainer: trainer,
      attended: attended,
      notes: notes
    });
  }
  
  // Ordenar por fecha (más reciente primero)
  result.sort((a, b) => {
    const dateA = new Date(a.date.split('/').reverse().join('-'));
    const dateB = new Date(b.date.split('/').reverse().join('-'));
    return dateB - dateA;
  });
  
  return result;
};

export const createAppointment = async (appointmentData) => {
  try {
    const response = await api.post('/appointments', appointmentData);
    return response.data;
  } catch (error) {
    console.error('Error en createAppointment:', error);
    throw error;
  }
};

export const updateAppointment = async (id, appointmentData) => {
  try {
    const response = await api.put(`/appointments/${id}`, appointmentData);
    return response.data;
  } catch (error) {
    console.error(`Error en updateAppointment con id ${id}:`, error);
    throw error;
  }
};

// Función para guardar localmente el estado de asistencia para desarrollo
export const saveAttendanceLocally = (id, attended) => {
  try {
    // Obtener datos existentes
    const storedAttendance = localStorage.getItem('appointmentAttendance');
    const attendanceData = storedAttendance ? JSON.parse(storedAttendance) : {};
    
    // Actualizar datos
    attendanceData[id] = attended;
    
    // Guardar en localStorage
    localStorage.setItem('appointmentAttendance', JSON.stringify(attendanceData));
    
    return true;
  } catch (error) {
    console.error('Error guardando asistencia localmente:', error);
    return false;
  }
};